/**
 * LensPill — the lens readout + picker (P3 SP1 rebuild of Task 6.3).
 *
 * READOUT: mode label (+ active names, or the pinned trail's display label).
 * CLICK opens the picker dropdown (a portaled UnifiedContextMenu):
 *   - "Canopy — show everything" (clears, closes)
 *   - COMPANIONS checklist (toggles the lens set; STAYS OPEN)
 *   - TRAILS picker (pins trail mode; closes). Single-trail sessions get a
 *     disabled "Only one trail in this session" row.
 * An × button appears beside the pill whenever a lens is active (one-click
 * clear); right-click on the pill still clears (power-user shortcut).
 * Escape closes the dropdown and returns focus to the pill.
 *
 * The old click-to-cycle behavior retired with the derived-mode invariant
 * (mode derives from set/trail inside setLens — see LensContext). No
 * keyboard shortcuts are wired (the old docstring's Cmd-L/arrows claim was
 * never implemented; shortcuts are a named follow-up).
 */
import React, { useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { CouncilContext } from '../../contexts/CouncilContext';
import { useLens } from '../../hooks/useLens';
import { useSessionTrails } from '../../hooks/useSessionTrails';
import { UnifiedContextMenu, MenuItem } from '../ui/UnifiedContextMenu';

const MODE_LABEL: Record<string, string> = {
  canopy: 'Canopy',
  single: 'Single',
  multi: 'Multi',
  trail: 'Trail',
};

export interface LensPillProps {
  /** Optional className appended to the pill's container. */
  className?: string;
}

export function LensPill({ className }: LensPillProps) {
  const { mode, activePersonaIds, activeTrailId, setLens, clear } = useLens();
  const ctx = useContext(CouncilContext);
  const trails = useSessionTrails();
  const btnRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);

  const close = () => { setPos(null); btnRef.current?.focus(); };

  useEffect(() => {
    if (!pos) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [pos]); // eslint-disable-line react-hooks/exhaustive-deps

  const open = (e: React.MouseEvent) => {
    e.stopPropagation();
    const r = btnRef.current?.getBoundingClientRect();
    setPos(r ? { x: r.left, y: r.bottom + 4 } : { x: e.clientX, y: e.clientY });
  };

  const togglePersona = (pid: string) => {
    const next = new Set(activePersonaIds);
    if (next.has(pid)) next.delete(pid); else next.add(pid);
    setLens({ activePersonaIds: next });
  };

  const roster = (ctx?.state?.roster ?? []).filter((r: any) => !r.dismissed_at);
  const items: MenuItem[] = [
    { label: 'Canopy — show everything', onClick: () => { clear(); close(); } },
  ];
  if (roster.length > 0) {
    items.push({ type: 'header', label: 'COMPANIONS' });
    for (const r of roster) {
      const pid = String((r as any).persona_id);
      items.push({
        label: ctx?.state?.personasById?.[pid]?.name ?? pid.slice(0, 8),
        checked: activePersonaIds.has(pid),
        onClick: () => togglePersona(pid), // checklist: stays open
      });
    }
  }
  items.push({ type: 'header', label: 'TRAILS' });
  if (trails.length <= 1) {
    items.push({ label: 'Only one trail in this session', disabled: true, onClick: undefined });
  } else {
    for (const t of trails) {
      items.push({
        label: t.label,
        checked: activeTrailId === t.id,
        onClick: () => { setLens({ mode: 'trail', activeTrailId: t.id }); close(); },
      });
    }
  }

  const activeNames = Array.from(activePersonaIds)
    .map(id => ctx?.state?.personasById?.[id]?.name ?? id.slice(0, 6))
    .slice(0, 3);
  const trailLabel = activeTrailId
    ? (trails.find(t => t.id === activeTrailId)?.label ?? 'Trail')
    : null;
  const subtitle = mode === 'canopy'
    ? null
    : mode === 'trail'
      ? trailLabel
      : activeNames.length > 0 ? activeNames.join(', ') : null;
  const lensActive = mode !== 'canopy' || activePersonaIds.size > 0 || !!activeTrailId;

  return (
    <span className="inline-flex items-center gap-0.5">
      <button
        ref={btnRef}
        type="button"
        data-testid="lens-pill"
        aria-label={`Lens: ${MODE_LABEL[mode] ?? mode}`}
        aria-haspopup="menu"
        aria-expanded={!!pos}
        onClick={open}
        onContextMenu={(e) => { e.preventDefault(); clear(); }}
        className={`inline-flex items-center gap-1.5 rounded-full
                    px-2.5 py-1 text-xs
                    bg-gray-100 dark:bg-gray-800
                    text-gray-700 dark:text-gray-200
                    hover:bg-gray-200 dark:hover:bg-gray-700
                    focus:outline-none focus:ring-2 focus:ring-indigo-400
                    transition-colors ${className ?? ''}`}
      >
        <span data-testid="lens-pill-mode" className="font-medium">
          {MODE_LABEL[mode] ?? mode}
        </span>
        {subtitle && (
          <span
            data-testid="lens-pill-subtitle"
            className="text-gray-500 dark:text-gray-400 truncate max-w-[180px]"
          >
            · {subtitle}
          </span>
        )}
      </button>
      {lensActive && (
        <button
          type="button"
          data-testid="lens-pill-clear"
          aria-label="Clear lens"
          onClick={clear}
          className="px-1 rounded-full text-xs text-gray-400
                     hover:bg-gray-200 dark:hover:bg-gray-700
                     hover:text-gray-600 dark:hover:text-gray-200"
        >
          ×
        </button>
      )}
      {pos && createPortal(
        <div data-testid="lens-pill-menu">
          <UnifiedContextMenu x={pos.x} y={pos.y} items={items} onClose={close} />
        </div>, document.body)}
    </span>
  );
}
